import React, { useEffect, useState } from "react";
import {
  Box, TextField, Button, Typography, Grid, Avatar, Divider,
  Snackbar, Alert, Chip, CircularProgress,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { hub } from "../../api/requests/hubs/hubs";
import { Colors } from "../../Theme/Theme";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GetValidatedTokenData from "../../utils/helper";

const Profile = () => {
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [panVerified, setPanVerified] = useState(false);
  const [panLoading, setPanLoading] = useState(false);
  const [panName, setPanName] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [formData, setFormData] = useState({
    pan_number: "",
    aadhar_number: "",
    bank_account_number: "",
    ifsc_code: "",
    upi_linkedin_number: "",
  });

  useEffect(() => {
    const tokenData = GetValidatedTokenData();
    if (tokenData?.userPayload?.name) setUserName(tokenData.userPayload.name);

    const fetchDetails = async () => {
      try {
        const data = await hub.getUsersDetails();
        setUserId(data?.data?.id);
        const d = data?.data;
        setFormData({
          pan_number: d?.pan_number || "",
          aadhar_number: d?.aadhar_number || "",
          bank_account_number: d?.bank_account_number || "",
          ifsc_code: d?.ifsc_code || "",
          upi_linkedin_number: d?.upi_linkedin_number || "",
        });
        if (d?.pan_number && d?.pan_number.length === 10) {
          setPanVerified(true);
          setPanName(d?.pan_name || tokenData?.userPayload?.name || "Verified");
        }
      } catch (error) {}
    };
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleVerifyPan = async () => {
    const pan = formData.pan_number?.toUpperCase();
    if (!pan || pan.length !== 10) {
      setSnackbar({ open: true, message: "Please enter a valid 10-character PAN number", severity: "error" });
      return;
    }
    setPanLoading(true);
    try {
      const response = await hub.getUsersDetails();
      setPanVerified(true);
      setPanName(userName || "PAN Holder");
      setSnackbar({ open: true, message: "PAN verified successfully!", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "PAN verification failed. Please try again.", severity: "error" });
    } finally {
      setPanLoading(false);
    }
  };

  const handleSubmit = async () => {
    const requestBody = { userId, ...formData };
    try {
      const response = await hub.patchUpdateUserDetails(requestBody);
      if (response) {
        setSnackbar({ open: true, message: "KYC details updated successfully!", severity: "success" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update details. Please try again.", severity: "error" });
    }
  };

  const handleUpdatePassword = async () => {
    if (!userId || !passwordValue) return;
    try {
      const response = await hub.patchUpdateUserPassword(userId, { newPassword: passwordValue });
      if (response) {
        setSnackbar({ open: true, message: "Password updated successfully!", severity: "success" });
        setPasswordValue("");
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update password.", severity: "error" });
      setPasswordValue("");
    }
  };

  const kycComplete = formData.pan_number && formData.aadhar_number && formData.bank_account_number && formData.ifsc_code;

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px", fontSize: "14px",
      "& fieldset": { borderColor: Colors.dividerColor },
      "&:hover fieldset": { borderColor: Colors.primary },
      "&.Mui-focused fieldset": { borderColor: Colors.primary, borderWidth: "2px" },
    },
    "& .MuiInputLabel-root": { fontSize: "13px", color: Colors.secondaryColor },
    "& .MuiInputLabel-root.Mui-focused": { color: Colors.primary },
  };

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 900 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <VerifiedUserIcon sx={{ fontSize: 22, color: Colors.primary }} />
            <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              KYC & Settings
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px" }}>
            Complete your KYC verification and manage account settings.
          </Typography>
        </Box>

        {/* KYC Status Banner */}
        <Box sx={{
          p: 2.5, borderRadius: "12px", mb: 3,
          backgroundColor: kycComplete ? Colors.greenSoft : "#FFFBEB",
          border: `1px solid ${kycComplete ? Colors.greenBorder : "#FDE68A"}`,
          display: "flex", alignItems: "center", gap: 2,
        }}>
          <Avatar sx={{
            width: 40, height: 40,
            backgroundColor: kycComplete ? Colors.insuranceGreen : "#F59E0B",
          }}>
            {kycComplete ? <CheckCircleIcon sx={{ fontSize: 22, color: "#FFFFFF" }} /> : <WarningAmberIcon sx={{ fontSize: 22, color: "#FFFFFF" }} />}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "14px", color: Colors.primaryTextColor }}>
              {kycComplete ? "KYC Complete" : "KYC Incomplete"}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: Colors.secondaryColor }}>
              {kycComplete ? "Your identity verification is complete." : "Please fill in your PAN, Aadhaar, and bank details to complete KYC."}
            </Typography>
          </Box>
          <Chip
            label={kycComplete ? "Verified" : "Pending"}
            size="small"
            sx={{
              height: 24, fontWeight: 600, fontSize: "11px",
              backgroundColor: kycComplete ? Colors.insuranceGreen : "#F59E0B",
              color: "#FFFFFF",
            }}
          />
        </Box>

        <Grid container spacing={3}>
          {/* PAN Verification */}
          <Grid item xs={12}>
            <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, overflow: "hidden" }}>
              <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <CreditCardIcon sx={{ fontSize: 18, color: Colors.primary }} />
                <Typography sx={{ fontWeight: 700, fontSize: "15px", color: Colors.primaryTextColor }}>
                  PAN Verification
                </Typography>
                {panVerified && (
                  <Chip label="Verified" size="small" sx={{ height: 20, fontSize: "10px", fontWeight: 600, backgroundColor: Colors.greenSoft, color: Colors.insuranceGreen }} />
                )}
              </Box>
              <Divider sx={{ borderColor: Colors.dividerColor }} />
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth size="small" label="PAN Number" name="pan_number"
                      value={formData.pan_number} onChange={handleChange}
                      inputProps={{ style: { textTransform: "uppercase" }, maxLength: 10 }}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      fullWidth variant="outlined" onClick={handleVerifyPan}
                      disabled={panLoading || !formData.pan_number}
                      sx={{
                        borderColor: Colors.primary, color: Colors.primary, fontWeight: 600,
                        fontSize: "13px", borderRadius: "10px", textTransform: "none", py: 0.85,
                        "&:hover": { backgroundColor: Colors.blueSoft },
                      }}
                    >
                      {panLoading ? <CircularProgress size={18} /> : "Verify PAN"}
                    </Button>
                  </Grid>
                  {panVerified && (
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 1.5, borderRadius: "8px", backgroundColor: Colors.greenSoft, border: `1px solid ${Colors.greenBorder}` }}>
                        <Typography sx={{ fontSize: "10px", fontWeight: 600, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Name on PAN
                        </Typography>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: Colors.primaryTextColor }}>
                          {panName}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </Grid>

          {/* KYC Details */}
          <Grid item xs={12}>
            <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, overflow: "hidden" }}>
              <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <AccountBalanceIcon sx={{ fontSize: 18, color: Colors.primary }} />
                <Typography sx={{ fontWeight: 700, fontSize: "15px", color: Colors.primaryTextColor }}>
                  Bank & Identity Details
                </Typography>
              </Box>
              <Divider sx={{ borderColor: Colors.dividerColor }} />
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Aadhaar Number" name="aadhar_number" type="number" value={formData.aadhar_number} onChange={handleChange} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Bank Account Number" name="bank_account_number" value={formData.bank_account_number} onChange={handleChange} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="IFSC Code" name="ifsc_code" value={formData.ifsc_code} onChange={handleChange} inputProps={{ style: { textTransform: "uppercase" } }} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="UPI Linked Number" name="upi_linkedin_number" value={formData.upi_linkedin_number} onChange={handleChange} sx={fieldSx} />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained" onClick={handleSubmit}
                    sx={{
                      backgroundColor: Colors.primary, fontWeight: 600, fontSize: "14px",
                      borderRadius: "10px", px: 4, py: 1, textTransform: "none", boxShadow: "none",
                      "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "0 4px 12px rgba(37,99,235,0.25)" },
                    }}
                  >
                    Save KYC Details
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Password */}
          <Grid item xs={12}>
            <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, overflow: "hidden" }}>
              <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <LockIcon sx={{ fontSize: 18, color: Colors.primary }} />
                <Typography sx={{ fontWeight: 700, fontSize: "15px", color: Colors.primaryTextColor }}>
                  Change Password
                </Typography>
              </Box>
              <Divider sx={{ borderColor: Colors.dividerColor }} />
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" label="New Password" type="password"
                      value={passwordValue} onChange={(e) => setPasswordValue(e.target.value)}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      fullWidth variant="contained" onClick={handleUpdatePassword}
                      disabled={!passwordValue}
                      sx={{
                        backgroundColor: Colors.primary, fontWeight: 600, fontSize: "13px",
                        borderRadius: "10px", textTransform: "none", py: 0.85, boxShadow: "none",
                        "&:hover": { backgroundColor: "#1D4ED8" },
                        "&.Mui-disabled": { backgroundColor: "#E2E8F0", color: "#94A3B8" },
                      }}
                    >
                      Update Password
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: "100%", borderRadius: "10px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
